import { useEffect } from 'react';
import { AxiosError } from 'axios';
import { useGet } from 'hooks/useGet';
import { RequestStatus } from 'types/RequestStatus';

interface useCachedGetReturn<T = any> {
	data?: T;
	status: RequestStatus;
	error?: AxiosError<any> | undefined;
}

const DEFAULT_CACHE_DURATION_MINUTES = 10;

interface CacheEntry<T> {
	value: T;
	expiresAt: number;
}

const cache = new Map<string, CacheEntry<unknown>>();

const getFresh = <T>(url: string): T | undefined => {
	const entry = cache.get(url);
	if (!entry) return undefined;
	if (Date.now() > entry.expiresAt) {
		cache.delete(url);
		return undefined;
	}
	return entry.value as T;
};

/**
 * Shared across all hook instances, keyed by request url, so repeated mounts reuse the previous response instead of refetching it.
 */
export const useCachedGet = <T = any>(
	url: string,
	options: any = {},
	cacheDurationMinutes = DEFAULT_CACHE_DURATION_MINUTES,
): useCachedGetReturn<T> => {
	const { getData, data, error, status } = useGet<T>(url, options);
	const cached = url ? getFresh<T>(url) : undefined;

	useEffect(() => {
		if (!url || getFresh(url) !== undefined) return;

		getData().then((result) => {
			if (result !== undefined) {
				cache.set(url, {
					value: result,
					expiresAt: Date.now() + cacheDurationMinutes * 60 * 1000,
				});
			}
		});
	}, [url]);

	return {
		data: cached ?? data,
		error,
		status: cached ? RequestStatus.SUCCESS : status,
	};
};
